import { COMMA, ENTER, SPACE } from "@angular/cdk/keycodes";
import {
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import {
  FormControl,
  FormGroupDirective,
  NgForm,
  Validators,
} from "@angular/forms";
import {
  ErrorStateMatcher,
  MatChipInputEvent,
  MatChipList,
} from "@angular/material";
import { ClipboardService } from "ngx-clipboard";
import { Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";

@Component({
  selector: "app-email-chips-input",
  templateUrl: "./email-chips-input.component.html",
  styleUrls: ["./email-chips-input.component.scss"],
})
export class EmailChipsInputComponent
  implements OnInit, OnChanges, OnDestroy, DoCheck
{
  @Input() mode: string;
  @Input() emailControl: FormControl;
  @Input() label?: string;
  subscription: Subscription;
  formControl = new FormControl(Validators.email);
  visible = true;
  selectable = false;
  removable = true;
  addOnBlur = true;
  error = false;
  separatorKeysCodes: number[] = [ENTER, COMMA, SPACE];
  selectedEmails: string[] = [];

  readonly emailRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  @ViewChild("emailChipList") emailChipList: MatChipList;

  errorStateMatcher: ErrorStateMatcher = {
    isErrorState: (
      control: FormControl | null,
      form: FormGroupDirective | NgForm | null,
    ) => {
      return (
        !!(
          this.emailControl &&
          this.emailControl.invalid &&
          (this.emailControl.dirty || this.emailControl.touched)
        ) || this.error
      );
    },
  };

  @ViewChild("emailInput") emailInput: ElementRef<HTMLInputElement>;
  constructor(
    private clipboard: ClipboardService,
    private cdRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (!this.emailControl.value) {
      this.emailControl.setValue([]);
    } else if (
      typeof this.emailControl.value === "string" &&
      this.emailControl.value.length
    ) {
      this.selectedEmails = this.emailControl.value.split(",");
      this.validateEmails();
    }
    if (this.mode === "view") {
      this.formControl.disable();
      this.selectable = false;
      this.removable = false;
    } else {
      this.formControl.enable();
    }

    this.subscription = this.emailControl.valueChanges
      .pipe(debounceTime(200))
      .subscribe((value) => {
        if (typeof value === "string" && value.length) {
          this.selectedEmails = value.split(",");
        } else {
          this.selectedEmails = [];
        }
        this.validateEmails();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.emailControl && changes.emailControl.currentValue) {
      const value = changes.emailControl.currentValue.value;
      if (typeof value === "string" && value.length) {
        this.selectedEmails = value.split(",");
      } else {
        this.selectedEmails = [];
      }
      this.validateEmails();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngDoCheck() {
    if (
      typeof this.emailControl.value === "string" &&
      this.selectedEmails.join(",") !== this.emailControl.value
    ) {
      this.selectedEmails = this.emailControl.value.split(",");
      this.validateEmails();
      this.cdRef.detectChanges();
    }

    if (this.emailChipList) {
      const isError = this.errorStateMatcher.isErrorState(
        this.emailControl,
        null,
      );
      if (this.emailChipList.errorState !== isError) {
        this.emailChipList.errorState = isError;
        this.emailChipList.stateChanges.next();
      }
    }
  }

  add(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;

    // Add our email
    if ((value || "").trim()) {
      if (!value.toLowerCase().match(this.emailRegex)) {
        this.error = true;
        return;
      }
      this.error = false;
      this.selectedEmails.push(value.trim());
    }

    // Reset the input value
    if (input) {
      input.value = "";
    }

    this.formControl.setValue(null);
    this.emailControl.setValue(this.selectedEmails.join(","));
    this.emailControl.markAsDirty();
  }

  remove(email: string): void {
    this.selectedEmails = this.emailControl.value.split(",");
    const index = this.selectedEmails.indexOf(email);
    if (index >= 0) {
      this.selectedEmails.splice(index, 1);
    }
    this.emailControl.setValue(this.selectedEmails.join(","));
    this.emailControl.markAsDirty();
  }

  validateEmails() {
    this.error = this.selectedEmails.some(
      (email) => !email.toLowerCase().match(this.emailRegex),
    );
  }

  copy() {
    this.clipboard.copyFromContent(this.selectedEmails.join(", "));
  }

  onBlur() {
    this.emailControl.markAsTouched();
  }
}
